/**
 * <jgd-collapsible-text> — clamps its content to a maximum height and shows a
 * "show more / show less" toggle when the content actually overflows.
 *
 * Markup contract:
 * - `data-max-height` attribute: clamp height in px.
 * - `.jgd-collapsible__content`: the wrapper that gets clamped (max-height is
 *   managed inline by this element; the fade hint is pure CSS).
 * - `.jgd-collapsible__toggle`: button, server-rendered with `hidden`.
 *
 * States on the host element (styled in the block's stylesheet):
 * - `data-overflowing`: content exceeds the clamp — toggle + fade are shown.
 * - `data-expanded`: user expanded the content.
 *
 * The clamp itself is server-rendered CSS (`--jgd-collapse-max-height` +
 * `overflow-y: auto`), so the height is stable from first paint (no CLS) and
 * the no-JS fallback is an accessible inline-scroll region. This element only
 * upgrades the UX: it swaps scrolling for the fade + toggle pattern when the
 * content overflows, and releases the clamp entirely when the content only
 * slightly exceeds it (< TOLERANCE) so the toggle never reveals just a line
 * or two.
 */

/** Extra overflow (px) required before the clamp kicks in. */
const TOLERANCE = 48;

class JgdCollapsibleText extends HTMLElement {
  /** @type {AbortController} */
  #controller;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const content = this.#content;

    this.#toggleButton?.addEventListener('click', this.#onToggle, { signal });

    // The no-JS scroll region is keyboard-focusable; with the JS upgrade the
    // box never scrolls, so the tab stop would be noise.
    content?.removeAttribute('tabindex');

    // Re-measure when the natural content size changes (fonts, images in the
    // rich text, viewport width — or, in the jgd-collapsible-group wrapper,
    // a child block changing height, e.g. an accordion row opening).
    // Observing the content's CHILDREN — not the clamped wrapper itself — so
    // our own clamping never re-triggers this. All children, not just the
    // first: the group wrapper can hold several blocks.
    if (content?.children.length) {
      this.#resizeObserver = new ResizeObserver(this.#refresh);
      for (const child of content.children) {
        this.#resizeObserver.observe(child);
      }
    }

    // In the theme editor the text can be edited live.
    if (window.Shopify?.designMode && content) {
      new MutationObserver(this.#refresh).observe(content, { childList: true, subtree: true, characterData: true });
    }

    // After the transition to expanded, release the inline max-height so the
    // content follows later reflows naturally.
    content?.addEventListener(
      'transitionend',
      (event) => {
        if (event.target === content && event.propertyName === 'max-height' && this.#expanded) {
          content.style.maxHeight = 'none';
        }
      },
      { signal }
    );

    this.#refresh();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
  }

  /** @returns {HTMLElement | null} */
  get #content() {
    return this.querySelector('.jgd-collapsible__content');
  }

  /** @returns {HTMLButtonElement | null} */
  get #toggleButton() {
    return this.querySelector('.jgd-collapsible__toggle');
  }

  get #maxHeight() {
    const value = Number(this.dataset.maxHeight);
    return value > 0 ? value : 280;
  }

  get #expanded() {
    return this.hasAttribute('data-expanded');
  }

  #refresh = () => {
    const content = this.#content;
    const button = this.#toggleButton;
    if (!content || !button) return;

    const overflowing = content.scrollHeight > this.#maxHeight + TOLERANCE;
    this.toggleAttribute('data-overflowing', overflowing);
    button.hidden = !overflowing;

    if (!overflowing) {
      // Fits (or only exceeds the clamp within the tolerance) — release the
      // server-side CSS clamp so no scrollbar or cut-off ever shows.
      this.removeAttribute('data-expanded');
      button.setAttribute('aria-expanded', 'false');
      content.style.maxHeight = 'none';
    } else if (this.#expanded) {
      // Content changed while expanded (e.g. reflow, reduced-motion skipped
      // the transitionend) — make sure no stale pixel height clips it.
      content.style.maxHeight = 'none';
    } else {
      content.style.maxHeight = `${this.#maxHeight}px`;
    }
  };

  #onToggle = () => {
    const content = this.#content;
    const button = this.#toggleButton;
    if (!content || !button) return;

    if (this.#expanded) {
      // Collapse: from the current full height (max-height may be 'none')
      // down to the clamp, so the transition has concrete endpoints.
      content.style.maxHeight = `${content.scrollHeight}px`;
      // Force a reflow so the starting height is committed before animating.
      void content.offsetHeight;
      content.style.maxHeight = `${this.#maxHeight}px`;
      this.removeAttribute('data-expanded');
      button.setAttribute('aria-expanded', 'false');

      // If the block's top was scrolled past, bring it back into view so the
      // user isn't left staring at unrelated content further down the page.
      if (this.getBoundingClientRect().top < 0) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      this.setAttribute('data-expanded', '');
      button.setAttribute('aria-expanded', 'true');
      content.style.maxHeight = `${content.scrollHeight}px`;
    }
  };
}

if (!customElements.get('jgd-collapsible-text')) {
  customElements.define('jgd-collapsible-text', JgdCollapsibleText);
}
